'use client'

import { ProjectDetailFragment } from '@/gql/generated/graphql'

import { useNavBar } from '@components/nav/nav-provider'
import {
  ProjectTokensCarouselDisplay,
  ProjectTokensDisplay,
  ProjectTokensGridDisplay
} from '@components/project-tokens-display'
import {
  AB500ShortLogo,
  AttributeIcon,
  BodyText,
  BrandText,
  CalendarPlusIcon,
  DetailText,
  DisplayText,
  HeadlineText
} from 'design-configuration/components'
import { motion } from 'motion/react'

import { STATUS_REFRESH_INTERVAL_SECONDS, useProjectStatus } from '@/hooks/use-project-status'
import { cn, truncatePriceString } from '@/lib/utils'
import { ProjectAddToCalendarButton, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { AllPurposeLink } from '@components/all-purpose-link'
import { buttonVariants } from '@components/button'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { formatDate } from '@utils/common'
import {
  getOpenSeaSeaDropProjectUrl,
  getVerticalTitle,
  isAb500,
  isMarfaResidency,
  isOpenSeaSeaDropProject,
  ValidStatusType
} from '@utils/project'
import { ab500DiscoverRoute, artistRoute, exploreRoute, projectPurchaseRoute, tokenShowRoute } from '@utils/routes'
import React from 'react'
import { formatEther } from 'viem'
import { LiveSaleDataDisplay } from '../purchase/components/live-sale-data-display'
import { useLiveSaleData } from '../purchase/components/project-sale-provider'
import { isSupportedMinterType } from '@artblocks/sdk/dist/machines/utils'
import { isOob } from '@components/solo-viewer/token-solo-viewer'
import { useRouter } from 'next/navigation'
import { GAS_WARS_CORE_CONTRACT_ADDRESS, GAS_WARS_PROJECT_ID, ONE_MILLION } from '@consts/common'
import { jackButcher } from 'public/fonts'

type ProjectHeaderProps = {
  project: ProjectDetailFragment
}

export const ProjectHeader = ({ project }: ProjectHeaderProps): React.ReactElement => {
  const router = useRouter()
  const { setSoloViewerData, isSoloViewerOpen } = useSoloViewer()
  const { status, refetchStatus } = useProjectStatus(project)

  React.useEffect(() => {
    const interval = setInterval(() => {
      refetchStatus()
    }, STATUS_REFRESH_INTERVAL_SECONDS * 1000)

    if (status === ValidStatusType.complete) {
      clearInterval(interval)
      return
    }

    return () => clearInterval(interval)
  }, [refetchStatus, status])

  useNavBar({
    fixed: false
  })

  const startDate = new Date(project.start_datetime ?? '')
  const artworkCount = status === ValidStatusType.paused ? project.invocations : project.max_invocations
  const isOpenEdition = project.max_invocations === ONE_MILLION

  return (
    <div
      className={cn(
        'p-md gap-x-2xl flex h-full min-h-[80dvh] w-full flex-col-reverse items-start justify-between lg:flex-row',
        jackButcher.variable
      )}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className="pt-md lg:px-md sticky top-[64px] flex max-h-screen w-full flex-col items-start justify-center text-left lg:h-[80vh] lg:w-1/3 lg:pt-[60px]"
      >
        {isAb500(project) ? (
          <AllPurposeLink href={ab500DiscoverRoute()} className="mb-md lg:mb-2xl">
            <AB500ShortLogo className="w-auto" />
          </AllPurposeLink>
        ) : null}
        <DetailText className="text-on-surface-variant font-medium uppercase">{getProjectTitle(project)}</DetailText>
        <DisplayText className="font-medium">{project.name}</DisplayText>
        <AllPurposeLink
          href={artistRoute(project.artistPage?.data?.[0]?.attributes?.slug ?? project.artist.public_address)}
        >
          <HeadlineText className="font-light">{project.artist_name}</HeadlineText>
        </AllPurposeLink>
        <BodyText className="mt-md lg:mt-2xl">
          {isOpenEdition && status !== ValidStatusType.complete && status !== ValidStatusType.paused
            ? 'Open edition'
            : `${artworkCount} unique artwork${artworkCount === 1 ? '' : 's'}`}
        </BodyText>
        {project.start_datetime ? (
          <BodyText>{`${status !== ValidStatusType.upcoming ? 'Released' : 'Releasing'} on ${
            status !== ValidStatusType.complete && status !== ValidStatusType.paused
              ? formatDate(startDate, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                  hour: 'numeric',
                  minute: 'numeric',
                  timeZoneName: 'short'
                }).replace(/, (\d+:\d+)/, ' at $1') // Replace the comma before the time with 'at'
              : formatDate(startDate, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })
          }`}</BodyText>
        ) : null}
        {status === ValidStatusType.complete || status === ValidStatusType.paused ? (
          <></>
        ) : (
          <PurchaseDisplay project={project} status={status} />
        )}
      </motion.div>
      <div className="h-fit w-full lg:h-full lg:w-2/3">
        <ProjectTokensDisplay
          project={project}
          onExpand={({ currentToken, tokens }) => {
            if (isOob(currentToken)) {
              setSoloViewerData({ token: currentToken, tokenList: tokens })
              return
            }

            router.push(tokenShowRoute(currentToken.contract_address, currentToken.token_id))
          }}
        >
          <ProjectTokensCarouselDisplay disableLiveView={isSoloViewerOpen} />
          <ProjectTokensGridDisplay />
        </ProjectTokensDisplay>
      </div>
    </div>
  )
}

const PurchaseDisplay = ({
  project,
  status
}: {
  project: ProjectDetailFragment
  status: ValidStatusType
}): React.ReactElement => {
  const liveSaleData = useLiveSaleData()

  const ONE_HOUR = 60 * 60 * 1000
  const now = new Date().getTime()
  const startTime = new Date(project?.auction_start_time ?? project.start_datetime ?? '').getTime()

  const isSupportedMinter = isSupportedMinterType(project.minter_configuration?.minter?.minter_type)
  const isLive = status === ValidStatusType.live || startTime - now < ONE_HOUR
  const showPurchaseButton = isLive && isSupportedMinter
  const showCalendarButton = !isLive
  const showOpenSeaSeaDropButton = isOpenSeaSeaDropProject(project.id)
  const isPostParamProject = project.pmp_project_configs?.length

  const isOpenEdition = project.max_invocations === ONE_MILLION

  const isGasWars = project.id === `${GAS_WARS_CORE_CONTRACT_ADDRESS}-${GAS_WARS_PROJECT_ID}`

  const isExploreAlgorithmDisabled = isGasWars

  return (
    <div className="gap-y-md mt-md flex w-full flex-col">
      <div className="gap-md flex w-full">
        <LiveSaleDataDisplay
          value={
            isOpenEdition
              ? '∞'
              : liveSaleData
                ? (liveSaleData.maxInvocations - liveSaleData.invocations).toString()
                : (project.max_invocations - (project.invocations ?? 0)).toString()
          }
          label="Works remaining"
          className="w-1/2"
        />
        <LiveSaleDataDisplay
          value={liveSaleData?.tokenPriceInWei ? truncatePriceString(formatEther(liveSaleData.tokenPriceInWei)) : '--'}
          label="Price"
          isPrice
          className="w-1/2"
        />
      </div>
      <div className="gap-y-xs flex flex-col">
        {isPostParamProject ? <PostParamsCallout /> : null}
        {showPurchaseButton ? (
          <AllPurposeLink
            href={projectPurchaseRoute(project.slug)}
            className={cn(
              'w-full',
              buttonVariants({
                colorTheme: 'primary',
                branded: true
              })
            )}
          >
            Proceed to purchase
          </AllPurposeLink>
        ) : null}

        {showCalendarButton ? (
          <ProjectAddToCalendarButton
            projects={[project]}
            colorTheme="secondary"
            branded
            buttonName={TrackedAddToCalButtonNames.projectHeader}
            className="w-full"
          >
            <div className="gap-x-xs flex items-center">
              <CalendarPlusIcon className="w-sm h-sm fill-inherit" />
              <BrandText>Add to calendar</BrandText>
            </div>
          </ProjectAddToCalendarButton>
        ) : null}

        {/* OpenSea SeaDrop projects get a special purchase button */}
        {showOpenSeaSeaDropButton ? <OpenSeaPurchaseButton project={project} status={status} /> : null}

        {isExploreAlgorithmDisabled ? null : (
          <AllPurposeLink
            href={exploreRoute(project.id)}
            className={cn(
              'w-full',
              buttonVariants({
                colorTheme: 'secondary',
                branded: true
              })
            )}
          >
            Explore algorithm
          </AllPurposeLink>
        )}
      </div>
    </div>
  )
}

function getProjectTitle(project: ProjectDetailFragment): string {
  if (isMarfaResidency(project.id)) {
    return 'Art Blocks × OpenSea Residency'
  }
  return getVerticalTitle(project)
}

function OpenSeaPurchaseButton({
  project,
  status
}: {
  project: ProjectDetailFragment
  status: ValidStatusType
}): React.ReactElement | null {
  const url = getOpenSeaSeaDropProjectUrl(project.id)
  if (!url) return null

  const isLive = status === ValidStatusType.live
  const buttonText = isLive ? 'Purchase on OpenSea' : 'View on OpenSea'
  const colorTheme = isLive ? 'primary' : 'secondary'
  return (
    <AllPurposeLink
      href={url}
      className={cn(
        'w-full',
        buttonVariants({
          colorTheme,
          branded: true
        })
      )}
    >
      {buttonText}
    </AllPurposeLink>
  )
}

function PostParamsCallout(): React.ReactElement {
  return (
    <div className="gap-x-2xs py-2xs flex w-full items-center justify-center">
      <AttributeIcon className="w-md h-md fill-on-surface-variant" />
      <BodyText className="text-on-surface-variant">Artwork parameters can change after purchase.</BodyText>
    </div>
  )
}
